import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, FileText, Trash2, CheckCircle2, ClipboardList, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { SYSTEM_FORM_TEMPLATES } from "@/data/system-forms";
import { useFormAI } from "@/hooks/useFormAI";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormRenderer } from "@/components/forms/FormRenderer";

export default function FormsTemplatesHub() {
  const navigate = useNavigate();
  const { user, orgId } = useAuth();
  const { generateForm, generating, prefillFormFromJob } = useFormAI();

  const [customForms, setCustomForms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("forms");
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiCategory, setAiCategory] = useState("inspection");

  // State for using forms and attaching to jobs
  const [fillFormOpen, setFillFormOpen] = useState(false);
  const [selectedFormTemplate, setSelectedFormTemplate] = useState<any | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [jobs, setJobs] = useState<any[]>([]);
  const [prefilledData, setPrefilledData] = useState<Record<string, any>>({});

  useEffect(() => {
    if (user && orgId) {
      loadCustomForms();
      loadJobs();
    }
  }, [user, orgId]);

  const loadCustomForms = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("form_templates")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_system_template", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCustomForms(data || []);
    } catch (error) {
      console.error("Error loading forms:", error);
      toast.error("Failed to load forms");
    } finally {
      setLoading(false);
    }
  };

  const loadJobs = async () => {
    if (!orgId) return;
    try {
      const { data, error } = await supabase
        .from("jobs")
        .select("id, job_number, status, property_address, owner_name, property_city, property_state, property_zip")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error("Error loading jobs for forms:", error);
    }
  };

  // Templates now use Templates.tsx page and TemplateBuilder

  const handleUseForm = async (formId: string) => {
    if (!orgId) {
      toast.error("Organization not found");
      return;
    }

    // Find in system templates first, then in custom forms
    const systemTemplate = SYSTEM_FORM_TEMPLATES.find((f) => f.id === formId);
    const customTemplate = customForms.find((f) => f.id === formId);

    const template = systemTemplate || (customTemplate
      ? { ...customTemplate, fields: customTemplate.form_fields }
      : null);

    if (!template) {
      toast.error("Form not found");
      return;
    }

    setSelectedFormTemplate(template);
    setSelectedJobId("");
    setPrefilledData({});
    setFillFormOpen(true);
  };

  // Prefill when job selected
  useEffect(() => {
    const prefill = async () => {
      if (!selectedFormTemplate || !selectedJobId) return;
      try {
        const data = await prefillFormFromJob(selectedJobId, selectedFormTemplate.fields);
        setPrefilledData(data || {});
      } catch (error) {
        console.error("Error prefilling form from job:", error);
      }
    };

    prefill();
  }, [selectedFormTemplate, selectedJobId, prefillFormFromJob]);

  const handleSubmitForm = async (formData: Record<string, any>) => {
    if (!selectedFormTemplate || !selectedJobId || !orgId) {
      toast.error("Please select a job before submitting");
      return;
    }

    try {
      const { error } = await supabase.from("form_submissions").insert({
        form_template_id: selectedFormTemplate.id,
        job_id: selectedJobId,
        org_id: orgId,
        submitted_data: formData,
        status: "submitted",
        submitted_at: new Date().toISOString(),
      });

      if (error) throw error;

      toast.success("Form submitted and attached to job");
      setFillFormOpen(false);
      setSelectedFormTemplate(null);
      setSelectedJobId("");
      setPrefilledData({});
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit form");
    }
  };

  const handleDeleteCustomForm = async (formId: string) => {
    if (!confirm("Are you sure you want to delete this form?")) return;

    try {
      const { error } = await supabase
        .from("form_templates")
        .delete()
        .eq("id", formId);

      if (error) throw error;

      toast.success("Form deleted");
      loadCustomForms();
    } catch (error) {
      console.error("Error deleting form:", error);
      toast.error("Failed to delete form");
    }
  };

  const handleCreateCustomForm = () => {
    setAiDialogOpen(true);
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please describe the form you want to create");
      return;
    }

    if (!orgId) {
      toast.error("Organization ID not found");
      return;
    }

    const result = await generateForm(aiPrompt, orgId, aiCategory);
    if (result) {
      setAiDialogOpen(false);
      setAiPrompt("");
      setAiCategory("inspection");
      loadCustomForms();
      setActiveTab("custom-forms");
    }
  };

  // Filter logic
  const filteredSystemForms = SYSTEM_FORM_TEMPLATES.filter((form) =>
    form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    form.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    form.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCustomForms = customForms.filter((form) =>
    form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    form.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      inspection: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      compliance: "bg-purple-500/10 text-purple-500 border-purple-500/20",
      customer: "bg-green-500/10 text-green-500 border-green-500/20",
      production: "bg-orange-500/10 text-orange-500 border-orange-500/20",
      insurance: "bg-red-500/10 text-red-500 border-red-500/20",
    };
    return colors[category] || "bg-gray-500/10 text-gray-500 border-gray-500/20";
  };

  return (
    <div className="flex-1 space-y-6 p-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Forms Library</h1>
        <p className="text-muted-foreground">
          Manage and create job forms for inspections, compliance, and more
        </p>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search forms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="forms">Form Library</TabsTrigger>
          <TabsTrigger value="custom-forms">My Custom Forms</TabsTrigger>
        </TabsList>

        {/* Form Library Tab */}
        <TabsContent value="forms" className="mt-6">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Industry-Standard Forms</h3>
              <p className="text-sm text-muted-foreground">
                Pre-built forms for inspections, compliance, and customer feedback
              </p>
            </div>
            <Button onClick={handleCreateCustomForm} className="gap-2">
              <Wand2 className="w-4 h-4" />
              Create Custom Form
            </Button>
          </div>

          {filteredSystemForms.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ClipboardList className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">No forms found</p>
                <p className="text-sm text-muted-foreground">Try adjusting your search</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSystemForms.map((form) => (
                <Card key={form.id} className="hover:shadow-lg transition-all hover:border-primary/50">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 rounded-lg bg-secondary/10">
                        <ClipboardList className="w-8 h-8 text-secondary" />
                      </div>
                      <Badge className={getCategoryColor(form.category)}>
                        {form.category}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl">{form.name}</CardTitle>
                    <CardDescription className="text-sm">
                      {form.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-secondary" />
                        <span>{form.fields.length} fields</span>
                      </div>
                      <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => handleUseForm(form.id)}
                      >
                        Use This Form
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Custom Forms Tab */}
        <TabsContent value="custom-forms" className="mt-6">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Your Custom Forms</h3>
              <p className="text-sm text-muted-foreground">
                Forms you've created for your organization
              </p>
            </div>
            <Button onClick={handleCreateCustomForm} className="gap-2">
              <Plus className="w-4 h-4" />
              Create Form
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Loading custom forms...</p>
            </div>
          ) : filteredCustomForms.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ClipboardList className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">No custom forms yet</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Create custom forms with our AI-powered form builder
                </p>
                <Button onClick={handleCreateCustomForm} className="gap-2">
                  <Wand2 className="w-4 h-4" />
                  Create Your First Form
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCustomForms.map((form) => (
                <Card key={form.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <ClipboardList className="w-8 h-8 text-primary" />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDeleteCustomForm(form.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <CardTitle className="mt-4">{form.name}</CardTitle>
                    <CardDescription>
                      {form.description || "No description"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      {form.updated_at
                        ? `Last updated: ${new Date(form.updated_at).toLocaleDateString()}`
                        : ""}
                    </p>
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={() => handleUseForm(form.id)}
                    >
                      Use This Form
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Fill Form Dialog - attach form to a job */}
      <Dialog open={fillFormOpen} onOpenChange={setFillFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedFormTemplate ? selectedFormTemplate.name : "Use Form"}
            </DialogTitle>
            <DialogDescription>
              Select a job to attach this form to, then fill it out.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-100px)] pr-4">
            <div className="space-y-4">
              <div>
                <Label>Attach to Job</Label>
                <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select a job">
                      {selectedJobId && jobs.find(j => j.id === selectedJobId) && (() => {
                        const job = jobs.find(j => j.id === selectedJobId)!;
                        const displayName = job.owner_name || "Unknown Customer";
                        const displayAddress = job.property_address 
                          ? `${job.property_address}${job.property_city ? `, ${job.property_city}` : ''}${job.property_state ? `, ${job.property_state}` : ''}`
                          : "No address";
                        return `${displayName} · ${displayAddress}`;
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((job) => {
                      const displayName = job.owner_name || "Unknown Customer";
                      const displayAddress = job.property_address 
                        ? `${job.property_address}${job.property_city ? `, ${job.property_city}` : ''}${job.property_state ? `, ${job.property_state}` : ''}`
                        : "No address";
                      const jobNumber = job.job_number || job.id.substring(0, 8);
                      
                      return (
                        <SelectItem key={job.id} value={job.id}>
                          <div className="flex flex-col items-start py-1">
                            <div className="font-medium">{displayName} · {displayAddress}</div>
                            <div className="text-xs text-muted-foreground">
                              Job: {jobNumber} {job.status ? `· ${job.status}` : ""}
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {selectedFormTemplate && (
                <FormRenderer
                  formName={selectedFormTemplate.name}
                  formDescription={selectedFormTemplate.description}
                  fields={selectedFormTemplate.fields}
                  initialData={prefilledData}
                  onSubmit={handleSubmitForm}
                  onSaveDraft={(data) => {
                    console.log("Saving draft:", data);
                    // TODO: Implement draft saving to database
                  }}
                />
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* AI Form Generation Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate Form with AI</DialogTitle>
            <DialogDescription>
              Describe the form you need and AI will create it for you
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="ai-prompt">What kind of form do you need?</Label>
              <Textarea
                id="ai-prompt"
                placeholder="e.g., 'Create a roof warranty registration form with customer details, roof specifications, and warranty terms'"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={4}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="ai-category">Form Category</Label>
              <Select value={aiCategory} onValueChange={setAiCategory}>
                <SelectTrigger id="ai-category" className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleAIGenerate}
              disabled={generating}
              className="w-full gap-2"
            >
              {generating ? (
                <>Generating Form...</>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" />
                  Generate Form with AI
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
